import React, { useEffect, useState } from "react";
import "./Roles.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import { UilEdit, UilTrashAlt, UilPlus, UilSave, UilTimes, UilLock } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissionsCatalog, setPermissionsCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const [openPermissionsRoleId, setOpenPermissionsRoleId] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Not persisted anywhere — must be re-typed for every save, same as Add Worker.
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode] = useState("");

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        userRequest.get("roles"),
        userRequest.get("permissions"),
      ]);
      setRoles(rolesRes.data.roles || []);
      setPermissionsCatalog(permissionsRes.data.permissions || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Erreur lors du chargement des rôles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;

    try {
      setCreating(true);
      await userRequest.post("roles", {
        name: newRoleName.trim(),
        description: newRoleDescription.trim(),
      });
      toast.success("Rôle créé avec succès !");
      setNewRoleName("");
      setNewRoleDescription("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de la création du rôle.");
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (role) => {
    setEditingRoleId(role._id);
    setEditName(role.name);
    setEditDescription(role.description || "");
  };

  const handleCancelEdit = () => {
    setEditingRoleId(null);
    setEditName("");
    setEditDescription("");
  };

  const handleSaveEdit = async (roleId) => {
    try {
      await userRequest.put(`roles/${roleId}`, {
        name: editName.trim(),
        description: editDescription.trim(),
      });
      toast.success("Rôle mis à jour.");
      handleCancelEdit();
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de la mise à jour du rôle.");
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Supprimer le rôle "${role.name}" ?`)) return;

    try {
      await userRequest.delete(`roles/${role._id}`);
      toast.success("Rôle supprimé.");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de la suppression du rôle.");
    }
  };

  const handleOpenPermissions = async (role) => {
    try {
      const res = await userRequest.get(`roles/${role._id}`);
      const granted = res.data.permissions || [];
      setSelectedPermissionIds(new Set(granted.map((p) => p._id)));
      setOpenPermissionsRoleId(role._id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des permissions.");
    }
  };

  const handleClosePermissions = () => {
    setOpenPermissionsRoleId(null);
    setSelectedPermissionIds(new Set());
  };

  const handleTogglePermission = (permissionId) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  // Button click just opens the passcode modal — the actual save only fires
  // once the code is confirmed there.
  const handleRequestSavePermissions = () => {
    setShowPasscodeModal(true);
  };

  const handleCancelPasscode = () => {
    setShowPasscodeModal(false);
    setPasscode("");
  };

  const handleConfirmSavePermissions = async () => {
    try {
      setSavingPermissions(true);
      await userRequest.put(`roles/${openPermissionsRoleId}/permissions`, {
        permissionIds: Array.from(selectedPermissionIds),
        creationPasscode: passcode,
      });
      toast.success("Permissions mises à jour.");
      setShowPasscodeModal(false);
      setPasscode("");
      handleClosePermissions();
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de l'enregistrement des permissions.");
    } finally {
      setSavingPermissions(false);
    }
  };

  const groupedPermissions = permissionsCatalog.reduce((groups, permission) => {
    const key = permission.module;
    if (!groups[key]) groups[key] = [];
    groups[key].push(permission);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="Roles">
        <div className="loading-container">
          <div className="loading-spinner-custom"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="Roles">
        <div className="error-message-custom">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="Roles">
      <h1>Rôles &amp; Permissions</h1>

      <form className="new-role-form" onSubmit={handleCreateRole}>
        <input
          placeholder="Nom du nouveau rôle"
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
          required
        />
        <input
          placeholder="Description (optionnel)"
          value={newRoleDescription}
          onChange={(e) => setNewRoleDescription(e.target.value)}
        />
        <button type="submit" className="action-icon-btn btn-primary" disabled={creating}>
          <UilPlus />
          {creating ? "Création..." : "Créer un rôle"}
        </button>
      </form>

      <TableContainer component={Paper} className="roles-table-container">
        <Table>
          <TableHead>
            <TableRow className="table-header">
              <TableCell sx={{ fontWeight: "bold" }}>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <React.Fragment key={role._id}>
                <TableRow>
                  <TableCell>
                    {editingRoleId === role._id ? (
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      role.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRoleId === role._id ? (
                      <input
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                      />
                    ) : (
                      role.description || "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {role.isSystem ? (
                      <span className="badge badge-system">
                        <UilLock size="14" /> Système
                      </span>
                    ) : (
                      <span className="badge badge-custom">Personnalisé</span>
                    )}
                  </TableCell>
                  <TableCell className="action-cell">
                    <div className="action-buttons-group">
                      <button
                        className="action-icon-btn btn-primary"
                        onClick={() => handleOpenPermissions(role)}
                      >
                        Gérer les permissions
                      </button>

                      {editingRoleId === role._id ? (
                        <>
                          <button className="action-icon-btn btn-edit" onClick={() => handleSaveEdit(role._id)}>
                            <UilSave />
                          </button>
                          <button className="action-icon-btn btn-delete" onClick={handleCancelEdit}>
                            <UilTimes />
                          </button>
                        </>
                      ) : (
                        !role.isSystem && (
                          <button className="action-icon-btn btn-edit" onClick={() => handleStartEdit(role)}>
                            <UilEdit />
                          </button>
                        )
                      )}

                      {!role.isSystem && editingRoleId !== role._id && (
                        <button className="action-icon-btn btn-delete" onClick={() => handleDeleteRole(role)}>
                          <UilTrashAlt />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>

                {openPermissionsRoleId === role._id && (
                  <TableRow>
                    <TableCell colSpan={4} className="permissions-editor-cell">
                      <div className="permissions-editor">
                        <div className="permissions-editor-header">
                          <h3>Permissions pour "{role.name}"</h3>
                          <button className="action-icon-btn btn-delete" onClick={handleClosePermissions}>
                            <UilTimes /> Fermer
                          </button>
                        </div>

                        <div className="permissions-grid">
                          {Object.entries(groupedPermissions).map(([module, perms]) => (
                            <div key={module} className="permissions-module">
                              <h4>{module}</h4>
                              {perms.map((perm) => (
                                <label key={perm._id} className="permission-checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={selectedPermissionIds.has(perm._id)}
                                    onChange={() => handleTogglePermission(perm._id)}
                                  />
                                  {perm.action}
                                </label>
                              ))}
                            </div>
                          ))}
                        </div>

                        <button
                          className="action-icon-btn btn-primary"
                          onClick={handleRequestSavePermissions}
                          disabled={savingPermissions}
                        >
                          <UilSave />
                          {savingPermissions ? "Enregistrement..." : "Enregistrer les permissions"}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showPasscodeModal} onClose={handleCancelPasscode}>
        <DialogTitle>Code de sécurité requis</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            type="password"
            label="Code de sécurité admin"
            fullWidth
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && passcode && !savingPermissions) handleConfirmSavePermissions();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPasscode} disabled={savingPermissions}>Annuler</Button>
          <Button
            onClick={handleConfirmSavePermissions}
            variant="contained"
            disabled={!passcode || savingPermissions}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default Roles;
